import type {CSSProperties} from 'react';
import type {DocumentVisual} from './types';

const TURN_DELAY = 0.935;
const CARD_TURN_DURATION = 0.9;
const PASSPORT_OPEN_DURATION = 1.5;
const CHECK_GAP = 1.45;
const COVER_ASPECT_RATIO = 0.75;
const SETTLE_DURATION = 1.2;
const REPLAY_PAUSE = 12;

type PrepCheck = 'side' | 'place' | 'lines' | 'glare';

type PrepTimeline = {
  checks: Record<PrepCheck, number>;
  replayAfter: number;
  style: CSSProperties;
};

const seconds = (value: number) => `${value.toFixed(3)}s`;
const percent = (value: number) => `${value}%`;

function getPrepTimeline(visual: DocumentVisual, aspectRatio: number): PrepTimeline {
  const isPassport = visual.kind === 'passport';
  const turnDuration = isPassport ? PASSPORT_OPEN_DURATION : CARD_TURN_DURATION;
  const turnEnd = TURN_DELAY + turnDuration;
  const mistakesAt = TURN_DELAY + turnDuration / 2;

  const checks: Record<PrepCheck, number> = {
    side: turnEnd,
    place: turnEnd + CHECK_GAP,
    lines: turnEnd + 2 * CHECK_GAP,
    glare: turnEnd + 3 * CHECK_GAP,
  };
  const [mrzX, mrzY, mrzWidth, mrzHeight] = visual.mrz;

  const style = {
    '--turn-at': seconds(TURN_DELAY),
    '--turn-duration': seconds(turnDuration),
    '--mistakes-at': seconds(mistakesAt),
    '--flatten-at': seconds(checks.place - 0.7),
    '--hand-out-at': seconds(checks.lines - 0.55),
    '--side-at': seconds(checks.side),
    '--place-at': seconds(checks.place),
    '--lines-at': seconds(checks.lines),
    '--glare-at': seconds(checks.glare),
    '--mrz-x': percent(mrzX),
    '--mrz-y': percent(mrzY),
    '--mrz-width': percent(mrzWidth),
    '--mrz-height': percent(mrzHeight),
    '--cover-width-scale': aspectRatio.toFixed(4),
    '--cover-height-scale': (1 / COVER_ASPECT_RATIO).toFixed(4),
  } as CSSProperties;

  return {checks, replayAfter: checks.glare + SETTLE_DURATION + REPLAY_PAUSE, style};
}

export {getPrepTimeline};
export type {PrepCheck, PrepTimeline};
